import { useState } from "react"
import { router } from "@inertiajs/react"
import { Plus, Trash2, UserPlus } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import type { AssignableUser, AssignmentRole, PropertyUser, Suite } from "@/types"

interface Props {
  propertyId: number
  suites: Suite[]
  propertyUsers: PropertyUser[]
  assignableUsers: AssignableUser[]
  assignmentRoles: AssignmentRole[]
}

const roleLabel: Record<AssignmentRole, string> = {
  leasing_contact: "Leasing Contact",
  listing_broker: "Listing Broker",
  presented_by: "Presented By",
}

// System roles an admin can create from this panel (both are "assignable").
const userRoleLabel: Record<string, string> = {
  broker: "Broker",
  agent: "Agent",
}

export default function AssignedUsers({ propertyId, suites, propertyUsers, assignableUsers, assignmentRoles }: Props) {
  const [userId, setUserId] = useState("")
  const [role, setRole] = useState<AssignmentRole>("leasing_contact")
  const [scope, setScope] = useState<string>("all") // "all" = property-level default

  const [newUserOpen, setNewUserOpen] = useState(false)
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "agent" })

  const handleAssign = () => {
    if (!userId) return
    router.post(
      `/admin/properties/${propertyId}/property_users`,
      {
        property_user: {
          user_id: Number(userId),
          role,
          ...(scope !== "all" ? { suite_id: Number(scope) } : {}),
        },
      },
      { onSuccess: () => setUserId("") },
    )
  }

  const handleRemove = (id: number) => {
    if (confirm("Remove this assignment?")) {
      router.delete(`/admin/properties/${propertyId}/property_users/${id}`)
    }
  }

  const handleCreateUser = () => {
    if (!newUser.name || !newUser.email) return
    router.post(
      `/admin/users`,
      { user: newUser },
      {
        preserveScroll: true,
        onSuccess: () => {
          setNewUser({ name: "", email: "", role: "agent" })
          setNewUserOpen(false)
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle>Assigned Users</CardTitle>
        <Sheet open={newUserOpen} onOpenChange={setNewUserOpen}>
          <SheetTrigger render={<Button size="sm" variant="outline" />}>
            <UserPlus className="mr-2 h-4 w-4" /> New user
          </SheetTrigger>
          <SheetContent className="gap-0">
            <SheetHeader>
              <SheetTitle>Create user</SheetTitle>
              <SheetDescription>
                Add a broker or in-house agent. They receive an email to set their password, and become available to
                assign below.
              </SheetDescription>
            </SheetHeader>

            <div className="grid gap-4 p-4">
              <div className="grid gap-1.5">
                <Label htmlFor="new-user-name">Name</Label>
                <Input
                  id="new-user-name"
                  value={newUser.name}
                  onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))}
                  placeholder="Jane Doe"
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="new-user-email">Email</Label>
                <Input
                  id="new-user-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
                  placeholder="jane@example.com"
                />
              </div>

              <div className="grid gap-1.5">
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}>
                  <SelectTrigger>
                    <SelectValue>{(v) => userRoleLabel[v as string] ?? v}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="broker">Broker</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <SheetFooter className="flex-row justify-end">
              <SheetClose render={<Button variant="outline" />}>Cancel</SheetClose>
              <Button onClick={handleCreateUser} disabled={!newUser.name || !newUser.email}>
                Create user
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </CardHeader>
      <CardContent className="space-y-4">
        {propertyUsers.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No users assigned. Assign a leasing contact, listing broker, or presented-by broker below — they appear on this property's brochures.
          </p>
        ) : (
          <ul className="divide-y">
            {propertyUsers.map((pu) => (
              <li key={pu.id} className="flex items-center justify-between py-2">
                <div>
                  <span className="font-medium">{pu.user_name}</span>
                  <span className="text-muted-foreground ml-2 text-sm">{pu.user_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{roleLabel[pu.role]}</Badge>
                  {pu.suite_number ? <Badge variant="outline">Suite {pu.suite_number}</Badge> : null}
                  <Button size="sm" variant="ghost" onClick={() => handleRemove(pu.id)}>
                    <Trash2 className="text-destructive h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-4">
          <div className="grid gap-1">
            <Label>User</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Select user">
                  {(v) => {
                    const u = assignableUsers.find((au) => String(au.id) === v)
                    return u ? u.name : v
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {assignableUsers.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.name} ({userRoleLabel[u.role] ?? u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1">
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as AssignmentRole)}>
              <SelectTrigger>
                <SelectValue>{(v) => roleLabel[v as AssignmentRole]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {assignmentRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {roleLabel[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1">
            <Label>Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger>
                <SelectValue>
                  {(v) => {
                    if (v === "all") return "Whole property"
                    const s = suites.find((su) => String(su.id) === v)
                    return s ? `Suite ${s.suite_number}` : v
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Whole property</SelectItem>
                {suites.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    Suite {s.suite_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button type="button" onClick={handleAssign} disabled={!userId} className="w-full">
              <Plus className="mr-2 h-4 w-4" /> Assign
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
